/**
 * CheckboxCard
 *
 * Reusable bordered card for multi-select form options. Used by the product
 * picker and the routing destination picker. Renders a checkbox indicator +
 * label inside a bordered/colored Pressable.
 *
 * `descriptionPlacement` decides where an optional description goes. 'below'
 * (the default) keeps it outside the bordered region, where it does not fight
 * the selected-state background. 'inside' puts it in the card under the label,
 * which is what a long description needs: outside the border a paragraph has
 * no boundary of its own and only distance ties it to an option.
 *
 * Layouts:
 *   - inline:          [✓ Label (subtitle)]   -- single row inside the card
 *   - stacked, below:  [✓ Label]              -- card holds checkbox + label
 *                      Description text...      -- helper text below, no background
 *   - stacked, inside: [✓ Label              ] -- both within one bordered card
 *                      [  Description text...]
 */

import { Check } from 'lucide-react-native';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

type CheckboxCardProps = {
  testID?: string;
  label: string;
  inlineSubtitle?: string;
  description?: string;
  descriptionPlacement?: 'below' | 'inside';
  isSelected: boolean;
  isDisabled?: boolean;
  onSelect: () => void;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  className?: string;
};

const checkboxIndicatorClass = (isSelected: boolean) =>
  twJoin(
    'h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-2 transition-colors duration-150 ease-out motion-reduce:transition-none',
    isSelected ? 'border-primary-600 bg-primary-600' : 'border-outline-400 bg-transparent'
  );

const containerClass = (isSelected: boolean, isDisabled: boolean, alignTop: boolean) =>
  twJoin(
    'w-full flex-row gap-2 rounded-md border px-3 py-2 transition-colors duration-150 ease-out motion-reduce:transition-none',
    // A description inside the card makes it multi-line, so the checkbox rides
    // up to the label rather than centring against the whole block.
    alignTop ? 'items-start' : 'items-center',
    'web:data-[focus-visible=true]:ring-indicator-primary web:data-[focus-visible=true]:ring-1 web:data-[focus-visible=true]:ring-inset',
    isSelected
      ? 'border-primary-700 bg-primary-50 data-[hover=true]:border-primary-700 data-[hover=true]:bg-primary-100 data-[active=true]:bg-primary-200'
      : 'border-background-300 bg-background-0 data-[hover=true]:border-outline-400 data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100',
    isDisabled && 'opacity-50'
  );

export function CheckboxCard({
  testID,
  label,
  inlineSubtitle,
  description,
  descriptionPlacement = 'below',
  isSelected,
  isDisabled = false,
  onSelect,
  accessibilityLabel,
  accessibilityHint,
  className,
}: CheckboxCardProps) {
  const showDescriptionInside = !!description && descriptionPlacement === 'inside';

  return (
    <VStack className={twJoin('w-full gap-2', className)}>
      <Pressable
        testID={testID}
        onPress={onSelect}
        disabled={isDisabled}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: isSelected, disabled: isDisabled }}
        accessibilityLabel={accessibilityLabel ?? label}
        accessibilityHint={accessibilityHint}
        aria-checked={isSelected}
        aria-disabled={isDisabled || undefined}
        className={containerClass(isSelected, isDisabled, showDescriptionInside)}>
        <View
          className={twJoin(
            checkboxIndicatorClass(isSelected),
            showDescriptionInside && 'mt-[3px]'
          )}>
          {isSelected ? <Icon as={Check} size="2xs" className="text-typography-0" /> : null}
        </View>
        <VStack className="flex-1 gap-1">
          <HStack className="flex-wrap items-baseline gap-1">
            <Text
              className={twJoin(
                'text-sm',
                isSelected ? 'font-semibold text-primary-700' : 'font-medium text-typography-700'
              )}>
              {label}
            </Text>
            {inlineSubtitle ? (
              <Text
                className={twJoin(
                  'text-xs',
                  isSelected ? 'text-primary-600' : 'text-typography-600'
                )}>
                {inlineSubtitle}
              </Text>
            ) : null}
          </HStack>

          {showDescriptionInside ? (
            <Text
              className={twJoin(
                'text-xs leading-snug',
                isSelected ? 'text-primary-700' : 'text-typography-700'
              )}>
              {description}
            </Text>
          ) : null}
        </VStack>
      </Pressable>

      {description && !showDescriptionInside ? (
        <Text className="px-3 text-xs leading-snug text-typography-700">{description}</Text>
      ) : null}
    </VStack>
  );
}
